function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import * as Y from 'yjs';
import { chunk, cloneDeep } from 'lodash';
import { ElementModel } from './ElementModel';
import { EditorConfig } from '../../edit/EditorConfig';
import { MAX_LINE_POINTS } from '../../tool/LineTool';
export class LineModel extends ElementModel {
  get drawPoints() {
    if (this.localPoints.length > 0) {
      return this.localPoints;
    }
    return this.points;
  }
  get arrowSize() {
    return this.strokeWidth * 5 + 15;
  }
  get headArrow() {
    return this.root.get('headArrow');
  }
  set headArrow(value) {
    this.root.set('headArrow', value);
  }
  get tailArrow() {
    return this.root.get('tailArrow');
  }
  set tailArrow(value) {
    this.root.set('tailArrow', value);
  }
  constructor(root, scope, liveCursor, isPerformanceMode) {
    super(root, scope, liveCursor, isPerformanceMode);
    _defineProperty(this, "controlledPoints", []);
    _defineProperty(this, "clearLocalPointsWhenYPointsChange", true);
    _defineProperty(this, "shouldUseLocalPoints", true);
    _defineProperty(this, "localPointsPick", 99);
    _defineProperty(this, "item", null);
    if (this.root.doc && !this.root.has('type')) {
      this.root.set('type', 'arrow');
    }
    if (this.root.doc && !this.root.has('points')) {
      // points [minX, minY, width, height, radius]
      this.root.set('points', new Y.Array());
    }
    if (this.root.doc && !this.root.has('headArrow')) {
      this.root.set('headArrow', 'none');
    }
    if (this.root.doc && !this.root.has('tailArrow')) {
      this.root.set('tailArrow', 'normal');
    }
  }
  subDispose() {
    // ignore
  }
  onVectorUpdate() {
    if (!this.item) {
      return;
    }
    this.item.removeChildren();
    const path = this.renderLine();
    this.item.addChildren(path);
  }
  renderLine() {
    const matrix = new this.scope.Matrix(this.pointsMatrix);
    const papperPoints = chunk(this.drawPoints, 2).map(item => {
      return new this.scope.Point(item[0], item[1]).transform(matrix);
    });
    const path = new this.scope.Path();
    path.moveTo(papperPoints[0]);
    papperPoints.slice(1).forEach(item => {
      path.lineTo(item);
    });
    path.smooth({
      type: 'catmull-rom'
    });
    path.strokeCap = 'butt';
    const [head, tail] = this.createArrow(path);
    path.strokeColor = new this.scope.Color(this.strokeColor);
    [head, path, tail].forEach(item => {
      item.strokeColor = new this.scope.Color(this.strokeColor);
      item.dashArray = this.dashArray;
      item.strokeWidth = this.strokeWidth;
    });
    return [head, path, tail];
  }
  liveCursorPoint() {
    const yArray = this.root.get(ElementModel.KEYS.points);
    if (yArray.length < 2) {
      return null;
    }
    const len = yArray.length;
    const point = new this.scope.Point(yArray.get(len - 2), yArray.get(len - 1));
    return point.transform(new this.scope.Matrix(this.pointsMatrix));
  }
  createPaperItem() {
    this.item = new this.scope.Group();
    this.item.addChildren(this.renderLine());
  }
  createArrow(path) {
    const [headPath, tailPath] = [new this.scope.Path(), new this.scope.Path()];
    if (!path) {
      return [headPath, tailPath];
    }
    const headArrowStyle = this.root.get('headArrow');
    const tailArrowStyle = this.root.get('tailArrow');
    const arrowSize = Math.min(this.arrowSize, path.length / 5);
    if (tailArrowStyle !== 'none') {
      const tail = path.getPointAt(path.length - 1);
      if (tail) {
        const leftTop = new this.scope.Point(tail.x - arrowSize, tail.y - arrowSize / 2);
        const leftBottom = new this.scope.Point(tail.x - arrowSize, tail.y + arrowSize / 2);
        const arrowRotation = tail.subtract(path?.getPointAt(path.length - 2)).angle;
        tailPath.add(leftTop, tail, leftBottom);
        tailPath.rotate(arrowRotation, tail);
        tailPath.strokeJoin = 'miter';
      }
    }
    if (headArrowStyle !== 'none') {
      const head = path.getPointAt(0);
      if (head) {
        const arrowRotation = path.getPointAt(1).subtract(head).angle;
        headPath.add(new this.scope.Point(head.x + arrowSize, head.y - arrowSize / 2), head, new this.scope.Point(head.x + arrowSize, head.y + arrowSize / 2));
        headPath.rotate(arrowRotation, head);
        headPath.strokeJoin = 'miter';
      }
    }
    return [headPath, tailPath];
  }
  editorConfig() {
    const cfg = new EditorConfig();
    cfg.resizeModel = () => 'horizontal';
    if (this.controlledPoints.length === 0) {
      this.controlledPoints.push(...new Array(MAX_LINE_POINTS).fill(0).map((_, index) => {
        return new LineControlPoint(`line-${index}`, this, {
          index,
          total: MAX_LINE_POINTS
        }, this.scope);
      }));
      cfg.controlPoints = this.controlledPoints;
    } else {
      cfg.controlPoints = this.controlledPoints;
    }
    return cfg;
  }
  vectorKeys() {
    return [];
  }
  styleKeys() {
    return {
      include: ['headArrow', 'tailArrow', 'strokeWidth'],
      exclude: ['fillColor']
    };
  }
  onStyleKeyUpdate(_key) {
    this.onVectorUpdate();
  }
}
class LineControlPoint {
  constructor(name, model, options, scope) {
    _defineProperty(this, "position", null);
    _defineProperty(this, "name", void 0);
    _defineProperty(this, "model", void 0);
    _defineProperty(this, "options", void 0);
    _defineProperty(this, "scope", void 0);
    this.name = name;
    this.model = model;
    this.options = options;
    this.scope = scope;
  }
  getPosition() {
    if (!this.model.item) {
      return new this.model.scope.Point(0, 0);
    }
    const matrix = new this.model.scope.Matrix(this.model.pointsMatrix);
    const distance = this.options.index / (MAX_LINE_POINTS - 1);
    if (this.position) {
      return this.model.scope.project.view.projectToView(this.position.transform(matrix));
    }
    const points = this.model['drawPoints'];
    const from = {
      x: points[0],
      y: points[1]
    };
    const to = {
      x: points[points.length - 2],
      y: points[points.length - 1]
    };
    const clonedPath = new this.scope.Path.Line(from, to);
    this.position = clonedPath.getPointAt(clonedPath.length * distance);
    return this.model.scope.project.view.projectToView(this.position.transform(matrix));
  }

  // point: project 坐标系
  onPositionChange(point) {
    if (!this.model.item) {
      return;
    }
    const pointsMatrix = new this.model.scope.Matrix(this.model.pointsMatrix);
    const invertedPoint = point.transform(pointsMatrix.inverted());
    const points = this.model['drawPoints'];
    this.position = invertedPoint;
    const clonedPoints = cloneDeep(points);
    clonedPoints[this.options.index * 2] = invertedPoint.x;
    clonedPoints[this.options.index * 2 + 1] = invertedPoint.y;
    this.model.setPoints(clonedPoints);
  }
}